import {
  CheckCircleOutlined,
  CloudUploadOutlined,
  FieldTimeOutlined,
  FolderOpenOutlined,
  UnorderedListOutlined,
  YoutubeOutlined,
} from "@ant-design/icons";
import { captureException } from "@sentry/react";
import { Button, theme, Result, Typography } from "antd";
import { truncate } from "lodash-es";
import { useCallback } from "react";
import Table from "/src/components/Table";
import Card from "/src/components/Card";
import Router from "/src/constants/router";
import { VideoState } from "/src/constants/strings";
import useHttpClient from "/src/hooks/useHttpClient";
import { generateUrl } from "/src/utils/http";
import { getVideoProgressPercent } from "/src/utils/video";
// import { UploadContext } from '/src/constants/contexts';
import UploadProvider from "/src/components/UploadProvider";
import useUploadQuota from "/src/hooks/useUploadQuota";
import { UPLOAD_API_BASE_URL } from "/src/constants";

const humanizeSize = (bytes) => {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / 1024 ** i).toFixed(1)} ${units[i]}`;
};

function ListVideo() {
  const {
    token: { colorBgContainer },
  } = theme.useToken();
  const httpClient = useHttpClient({ baseURL: UPLOAD_API_BASE_URL });
  // const { isUploading } = useContext(UploadContext);
  const uploadingVideo = UploadProvider.useUploadingVideo();
  const { quota } = useUploadQuota();
  const uploadDisabled = Boolean(quota?.disabled);
  const fetchFn = useCallback(
    ({ pagination: { current, pageSize } }) =>
      httpClient
        .get(
          generateUrl({
            url: "/videos",
            query: {
              _page: current,
              _page_size: pageSize,
              _sort: "id",
              _order: "DESC",
            },
          })
        )
        .then(({ data: { data, total } }) => ({
          data,
          total,
        }))
        .catch(captureException),
    []
  );

  if (uploadingVideo) {
    Router.navigate(`/videos/${uploadingVideo.id}`);
    return null;
  }

  const columns = [
    { key: "id", title: "شناسه" },
    {
      key: "title",
      title: "عنوان",
      render: (title, { filename }) => truncate(title || filename, { length: 30 }),
    },
    {
      key: "state",
      title: "وضعیت",
      label: VideoState,
      render: Table.RenderTypes.TAG_FACTORY({
        key: "state",
        color: {
          PUBLISHED: "green",
          EXPIRED: "red",
        },
        icon: {
          PUBLISHED: <CheckCircleOutlined />,
          WAITING_FOR_PUBLISH: <FieldTimeOutlined />,
        },
      }),
    },
    {
      key: "progress",
      title: "درصد پیشرفت",
      render: (_, v) => {
        let percent;
        const { state } = v;
        if (state === "PUBLISHED") {
          percent = "100%";
        }
        percent = `${getVideoProgressPercent(v)}%`;
        return <Typography.Text code>{percent}</Typography.Text>;
      },
    },
    {
      key: "size",
      title: "حجم",
      render: (_, v) => {
        const { size } = v;
        return <Typography.Text code className="whitespace-nowrap">{humanizeSize(size)}</Typography.Text>;
      },
    },
    {
      key: "url",
      title: "لینک",
      render: (_, v) => {
        const { url } = v;
        return url ? (
          <a
            href={url}
            target="_blank"
            onClick={(e) => {
              e.isLink = true;
            }}
            rel="noopener noreferrer"
            aria-label="Open video on YouTube"
          >
            <YoutubeOutlined />
          </a>
        ) : (
          "-"
        );
      },
    },
    // {
    //   title: 'وضعیت فایل',
    //   key: 'fileState',
    //   render: Table.RenderTypes.TAG_FACTORY({
    //     key: 'fileState',
    //     color: {
    //       UPLOADED: 'green',
    //       UPLOAD_FAILED: 'error',
    //     },
    //     icon: {
    //       UPLOAD_FAILED: <WarningOutlined />,
    //       REMOVED: <WarningOutlined />,
    //     },
    //   }),
    //   label: VideoFileStateLabel,
    // },
    {
      title: "تاریخ ایجاد",
      key: "createdAt",
      render: Table.RenderTypes.DATE,
    },
  ];

  return (
    <Card
      actions={
        uploadDisabled ? null : (
          <Button
            type="primary"
            icon={<CloudUploadOutlined />}
            onClick={() => Router.navigate("/videos/new")}
          >
            آپلود ویدیو
          </Button>
        )
      }
      title="آپلودها"
      Icon={UnorderedListOutlined}
    >
      <Table
        columns={columns}
        fetchFn={fetchFn}
        className="mt-4"
        onRow={(record, rowIndex) => ({
          onClick: (event) => {
            if (event.isLink) {
              return;
            }
            Router.navigate(`/videos/${record.id}`);
          },
        })}
        rowKey="id"
        empty={
          <Result
            title="هنوز ویدویی آپلود نکردید"
            status="info"
            style={{
              marginTop: 20,
              wordBreak: "break-word",
            }}
            icon={<FolderOpenOutlined style={{ color: "gray" }} />}
          />
        }
      />
    </Card>
  );
}

export default ListVideo;
